import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Check, Copy, Download, Link2, LogOut, Trash2, Upload } from 'lucide-react';
import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { Button, Card, SectionHeader, Segmented } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { setLocale } from '@/lib/i18n';
import { cx, formatFullDate } from '@/lib/format';
import { parseImportFile, type ImportRowError } from '@/lib/importFile';
import {
  useCreateShare,
  useImportTransactions,
  useLogout,
  useMe,
  useRevokeShare,
  useShares,
  useUpdateProfile,
} from '@/lib/queries';
import { useTheme, type Theme } from '@/lib/theme';

export const Route = createFileRoute('/app/settings')({ component: Settings });

function Settings() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  const { data: me } = useMe();
  const { theme, setTheme } = useTheme();
  const updateProfile = useUpdateProfile();
  const logout = useLogout();

  const { data: shares } = useShares();
  const createShare = useCreateShare();
  const revokeShare = useRevokeShare();
  const [copied, setCopied] = useState<string | null>(null);

  const importTransactions = useImportTransactions();
  const importing = importTransactions.isPending;
  const [report, setReport] = useState<{
    inserted: number;
    skipped: number;
    errors: ImportRowError[];
  } | null>(null);

  /**
   * Parse first, send once.
   *
   * The endpoint validates the array as a whole and rejects it wholesale, so a
   * single malformed row would otherwise sink the other four hundred. Splitting
   * the file here means the bad lines are reported by line number and the rest
   * still land.
   */
  const onPickFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input, or picking the same file twice fires no change event.
    event.target.value = '';
    if (!file) return;

    const { rows, errors } = parseImportFile(await file.text(), me?.baseCurrency);
    if (rows.length === 0) {
      setReport({ inserted: 0, skipped: 0, errors });
      toast.show(t('settings.importNothing'));
      return;
    }

    try {
      const result = await importTransactions.mutateAsync({
        transactions: rows,
        skipDuplicates: true,
      });
      setReport({ inserted: result.inserted, skipped: result.skipped, errors });
      toast.show(t('settings.importDone', { inserted: result.inserted, skipped: result.skipped }));
    } catch (error) {
      toast.show(error instanceof Error ? error.message : t('errors.generic'));
    }
  };

  const language = (i18n.language.startsWith('en') ? 'en' : 'zh') as 'zh' | 'en';

  const copyLink = async (slug: string) => {
    const url = `${window.location.origin}/s/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(slug);
      toast.show(t('settings.copied'));
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard access needs a secure context and can be refused; showing the
      // URL is a worse experience than copying, but it is not a dead end.
      toast.show(url);
    }
  };

  return (
    <AppShell title={t('settings.title')}>
      <div className="mx-auto flex max-w-3xl flex-col pb-8">
        <SectionHeader>{t('settings.account')}</SectionHeader>
        <Card>
          <div className="flex items-center gap-3">
            <span
              className={cx(
                'flex size-12 items-center justify-center rounded-[13px] bg-[var(--accent)] text-[19px] font-semibold text-white shadow-sm',
                me?.isDemo && 'ring-1 ring-inset ring-white/35',
              )}
            >
              {(me?.displayName ?? me?.username ?? '?').slice(0, 1).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-[17px] font-semibold">
                  {me?.displayName ?? me?.username}
                </span>
                {me?.isDemo && (
                  <span className="shrink-0 rounded-[6px] bg-[var(--warning)]/15 px-2 py-0.5 text-[11px] font-medium text-[var(--warning)]">
                    {t('common.readOnly')}
                  </span>
                )}
              </div>
              <p className="truncate text-[13px] text-secondary">{me?.email}</p>
            </div>
          </div>

          <Button
            variant="secondary"
            className="mt-4 w-full gap-2"
            onClick={async () => {
              await logout.mutateAsync();
              await navigate({ to: '/login' });
            }}
          >
            <LogOut size={16} />
            {t('auth.signOut')}
          </Button>
        </Card>

        <SectionHeader>{t('settings.appearance')}</SectionHeader>
        <Card className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <span className="text-[15px]">{t('settings.theme')}</span>
            <Segmented<Theme>
              value={theme}
              onChange={setTheme}
              ariaLabel={t('settings.theme')}
              options={[
                { value: 'light', label: t('settings.themeLight') },
                { value: 'dark', label: t('settings.themeDark') },
                { value: 'system', label: t('settings.themeSystem') },
              ]}
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-[15px]">{t('settings.language')}</span>
            <Segmented<'zh' | 'en'>
              value={language}
              onChange={(next) => {
                setLocale(next);
                if (!me?.isDemo) updateProfile.mutate({ locale: next });
              }}
              ariaLabel={t('settings.language')}
              options={[
                { value: 'zh', label: '中文' },
                { value: 'en', label: 'English' },
              ]}
            />
          </div>
        </Card>

        <SectionHeader>{t('settings.share')}</SectionHeader>
        <Card>
          <p className="text-[13px] leading-relaxed text-secondary">{t('settings.shareHint')}</p>

          <Button
            variant="secondary"
            className="mt-3 w-full gap-2"
            disabled={me?.isDemo || createShare.isPending}
            onClick={() => createShare.mutate({ scope: 'all', expiresInDays: 30 })}
          >
            <Link2 size={16} />
            {t('settings.createShare')}
          </Button>

          {(shares ?? []).length > 0 && (
            <ul className="mt-4 flex flex-col gap-2">
              {shares!.map((share) => (
                <li
                  key={share.id}
                  className="flex items-center gap-2 rounded-control bg-[var(--fill-4)] px-3 py-2"
                >
                  <code className="min-w-0 flex-1 truncate text-[13px] text-secondary">
                    /s/{share.slug}
                  </code>
                  {share.expiresAt && (
                    <span className="tnum shrink-0 text-[11px] text-tertiary">
                      {formatFullDate(share.expiresAt.slice(0, 10), i18n.language)}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => void copyLink(share.slug)}
                    aria-label={t('settings.copied')}
                    className="press flex size-7 shrink-0 items-center justify-center rounded-full text-secondary"
                  >
                    {copied === share.slug ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => revokeShare.mutate(share.id)}
                    aria-label={t('settings.revoke')}
                    className="press flex size-7 shrink-0 items-center justify-center rounded-full text-secondary hover:text-[var(--danger)]"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <SectionHeader>{t('settings.data')}</SectionHeader>
        <Card className="flex flex-col gap-2">
          {/*
           * Plain links, not fetch-and-blob. The browser streams the download
           * and names the file from Content-Disposition; the object-URL route
           * would need a matching revokeObjectURL for no gain here.
           */}
          <a
            href={api.transactions.exportUrl('csv')}
            className="press flex items-center gap-3 rounded-control px-1 py-2.5 text-[17px]"
          >
            <Download size={18} className="text-secondary" />
            {t('settings.exportCsv')}
          </a>
          <a
            href={api.transactions.exportUrl('json')}
            className="press flex items-center gap-3 rounded-control px-1 py-2.5 text-[17px]"
          >
            <Download size={18} className="text-secondary" />
            {t('settings.exportJson')}
          </a>

          <div className="border-t border-separator pt-2">
            <label
              className={cx(
                'press flex items-center gap-3 rounded-control px-1 py-2.5 text-[17px]',
                me?.isDemo || importing ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
              )}
            >
              <Upload size={18} className="text-secondary" />
              {importing ? t('common.loading') : t('settings.import')}
              <input
                type="file"
                accept=".csv,.json,text/csv,application/json"
                disabled={me?.isDemo || importing}
                onChange={onPickFile}
                className="sr-only"
              />
            </label>
            <p className="px-1 pb-1 text-[13px] text-tertiary">{t('settings.importHint')}</p>

            {report && (
              <div className="mt-1 rounded-control bg-[var(--fill-4)] px-3 py-2 text-[13px]">
                <p className="text-secondary">
                  {t('settings.importDone', { inserted: report.inserted, skipped: report.skipped })}
                </p>
                {report.errors.length > 0 && (
                  <>
                    <p className="pt-1 text-[var(--warning)]">
                      {t('settings.importFailedRows', { count: report.errors.length })}
                    </p>
                    <ul className="pt-0.5 text-tertiary">
                      {/* First few only: a wall of identical messages helps nobody. */}
                      {report.errors.slice(0, 3).map((e) => (
                        <li key={e.line}>
                          {t('settings.importLine', { line: e.line })} · {e.message}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        </Card>

        <p className="px-1 pt-8 text-[13px] text-tertiary">{t('common.appNameFull')} · v0.1.0</p>
      </div>
    </AppShell>
  );
}
